/*
 * Ryan O'Dowd
 * 2024-01-22
 * © Copyright 2024 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  Link,
  useParams,
} from 'react-router';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  assignGoalToStudent,
  fetchBookNames,
  fetchBooks,
  fetchClassroomGoals,
  fetchClassroomStudents,
  updateClassroomStudent,
} from '@utils/store/actions';
import Badge from '@components/common/Badge';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import GoalPreview from './GoalPreview';
import IconButton from '@components/common/IconButton';
import InviteDialog from './InviteDialog';
import Menu from '@components/common/Menu';
import NewGoalDialog from './NewGoalDialog';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import Tooltip from '@components/common/Tooltip';
import {
  View,
} from 'react-native';
import WebPageStub from '@website/containers/WebPageStub';
import WebsiteSection from '@website/components/WebsiteSection';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';

const Classroom = () => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const params = useParams(); // @TODO: convert to useLocationState...and don't need duplicate query params for what's already part of the url (maybe remove from url?)
  const schoolId = +params.schoolId;
  const classroomId = +params.classroomId;

  const [_isStudentOverflowMenuOpen, setIsStudentOverflowMenuOpen] = useState(false);
  const [_selectedStudentId, setSelectedStudentId] = useState(null);
  const [_show404, setShow404] = useState(false);
  const [_isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [_isUnassignedGoalsDialogOpen, setIsUnassignedGoalsDialogOpen] = useState(false);

  const [_isNewGoalDialogOpen, setIsNewGoalDialogOpen] = useState(false);

  const classroom = hooks.useTypedSelector((state) => state.classrooms[classroomId] || {}, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue)); // @TODO: shouldn't need `{}`
  const classroomStudents = hooks.useTypedSelector((state) => state.classroomStudents[classroomId] || []).filter((student) => ['active', 'invited'].includes(student.status), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue)); // @TODO: shouldn't need `[]`
  const classroomGoals = hooks.useTypedSelector((state) => Object.values(state.classroomGoals).filter((goal: any) => {
    return goal.classroom_goal_metadata.classroom_id === classroomId;
  }), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  useEffect(() => {
    setTimeout(() => setShow404(true), 4000);
  }, []);

  useEffect(() => { // @TODO: this should go in App and be called coniditionally if user is logged in
    // @TODO: dispatch(fetchSchool(schoolId));
    dispatch(fetchBooks());
    dispatch(fetchBookNames());
    dispatch(fetchClassroomGoals(classroomId));
    dispatch(fetchClassroomStudents(classroomId));
  }, [dispatch, classroomId]);

  const studentTableRows = classroomStudents.map((cs) => { // @TODO: convert these to accordion? when expanded, show all info for just this student (and class average)
    let numUnassignedPublishedGoals = 0;
    if (cs.status === 'active') {
      numUnassignedPublishedGoals = classroomGoals.filter((goal: any) => { // @TODO: nesting this migh not be performant, but it also migh tnot make a difference
        if (goal.classroom_goal_metadata.status !== 'published') {
          return false;
        }
        return !goal.classroom_goal_metadata.student_ids_assigned?.includes(cs.user_id);
      }).length;
    }

    return [ // @TODO: update ids
      {
        id: `${cs.id}_display_name`,
        content: (
          <span>
            {cs.user?.display_name ? cs.user.display_name : cs.display_name}{/* @TODO: if user exists, show cs.display_name on hover for convenience? */}
          </span>
        ),
        sortValue: ((cs.user?.display_name ? cs.user.display_name : cs.display_name) || '').toLocaleLowerCase(),
      }, // @TODO: maybe surround with join date (or invitation date if pending)
      // @TODO: {id: `${cs.id}_date_invited`, content: cs.join_code ? <Tooltip title={dateFnsFormat(cs.created_at, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(cs.created_at, 'MMM d')}</span></Tooltip> : '-'}, // @TODO: replace '-' with tooltip about joining via public code?
      {
        id: `${cs.id}_date_joined`,
        content: cs.date_joined ? <Tooltip title={dateFnsFormat(cs.date_joined, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(cs.date_joined, 'MMM d')}</span></Tooltip> : '-',
        sortValue: new Date(cs.date_joined || 0).getTime(),
      },
      {
        id: `${cs.id}_status`,
        content: (
          <span>
            {cs.status === 'invited' &&
              <Tooltip title='Invitation pending: please have student enter this code in their account to join the classroom.'><span style={styles.joinCode}>{cs.join_code}</span></Tooltip>
            }
            {cs.status === 'active' &&
              <span>active</span>
            }{/* @TODO: prettier status column */}
            {cs.status === 'rejected' &&
              <span>invitation removed</span>
            }{/* @TODO: prettier status column */}
          </span>
        ),
        sortValue: cs.status.toLocaleLowerCase(),
      },
      {
        id: `${cs.id}_menu`,
        content: (
          (cs.status === 'invited' || !!numUnassignedPublishedGoals) && (// @TODO: temporary since invited students are the only ones with menu options right now
            <Menu
              statusBarHeight={styles.safeAreaInsets.top}// @TODO: abstract menu to oak component and abstract this prop away
              anchor={
                <span style={styles.smallCell}>
                  <IconButton
                    icon='dots-vertical'
                    iconColor={styles.colors.gray}
                    onPress={() => {
                      setIsStudentOverflowMenuOpen(true);
                      setSelectedStudentId(cs.id);
                    }}
                  />
                  <Badge// @TODO: this no longer works because it's not a child of IconButton anymore
                    visible={!!numUnassignedPublishedGoals}
                    top={-4}
                    right={-4}
                  >
                    {numUnassignedPublishedGoals}
                  </Badge>
                </span>
              }
              visible={_selectedStudentId === cs.id && !!_isStudentOverflowMenuOpen}
              onDismiss={() => {
                setIsStudentOverflowMenuOpen(false);
                setSelectedStudentId(null);
              }}
            >{/* @TODO: most menuitems can be conditionally rendered */}
              {/* @TODO:
              <Menu.Item
                title='Email parent'
                onPress={() => {}}// @TODO: implement
              >
              <Menu.Item
                title='Resend invitation'
                onPress={() => {}}// @TODO: implement
              />
              <Menu.Item
                title='Approve/reject join code'
                onPress={() => {}}// @TODO: implement
              />
              */}
              {!!numUnassignedPublishedGoals &&
                <Menu.Item
                  // @TODO: this is way too big of a menu when it's open on web
                  title='View unassigned goals'
                  onPress={() => {
                    setIsStudentOverflowMenuOpen(false);
                    setIsUnassignedGoalsDialogOpen(true);
                  }}
                  // @TODO: badge should be a prop
                  //<Badge visible={!!numUnassignedPublishedGoals}>
                  //  {numUnassignedPublishedGoals}
                  //</Badge>
                />
              }
              {cs.status === 'invited' &&
                <Menu.Item
                  title='Remove pending invitation'
                  // @TODO: dangerous prop
                  onPress={() => {
                    dispatch(updateClassroomStudent(classroomId, cs.id, {status: 'rejected'}));
                    setIsStudentOverflowMenuOpen(null);
                    setSelectedStudentId(null);
                  }}
                />
              }
              {/* @TODO: is this something i want to allow once invite is accepted?
              {cs.status === 'active' &&
                <Menu.Item
                  title={<span style={styles.dangerous}>Remove from classroom</span>}
                  // @TODO: dangerous should be a prop
                  onPress={() => {}}// @TODO: implement
                />
              }
              */}
            </Menu>
          )
        ),
      },
    ];
  });

  if (!classroom?.school?.name && _show404) {
    return (
      <WebPageStub>{/* @TODO: this isn't pretty...needs to be in a websitesection? */}
        <View style={styles.errorTextWrapper}>
          <Text style={[styles.errorText, styles.bold]}>This classroom could not be found.</Text>
          <Text style={styles.errorText}>Please check back later or visit the <Link to='/fundraising'>fundraising page</Link> to search partners.</Text>{/* @TODO: update */}
          <Text style={styles.errorText}>Please check back later.</Text>{/* @TODO: update */}
        </View>
      </WebPageStub>
    );
  }

  return (
    <WebPageStub
      hideTabs={true}// @TODO: instead of hiding tabs, should we show different tabs for teacher dashboard? i think we should consider it once this single page becomes too complex
    >
      <WebsiteSection
        style={styles.noVerticalPadding}
        isDarkBackground={false}
      >
        <h1 style={styles.h1}>{classroom?.school?.name || 'Loading...'}<br />{classroom?.name || 'Loading...'}{/* @TODO: update */}</h1>
      </WebsiteSection>

      {/* @TODO: if no students, show better empty screen instead of blank table */}
      {classroomId !== 1 &&
        <WebsiteSection
          isDarkBackground={false}
          title={(
            <span style={styles.titleRow}>
              <span>Roster</span>
            </span>
          )}
        >
          <Table// @TODO: rows shouldn't have pointer on hover unless they're selectable
            tableTitle='Students'
            open={true}// @TODO: default to false, but have badge for notifications if there are students to approve, for example
            headerRowData={[
              {width: 250, id: 'name', content: 'Name'},
              {width: 150, id: 'date_joined', content: 'Date joined', rightAlign: true},
              {width: 100, id: 'status', content: 'Status', rightAlign: true},
              {width: 50, id: 'action', content: '', rightAlign: true},
            ]}
            isSortable={true}
            orderByIndex={1}
            rowData={studentTableRows}
            // @TODO: onRowPress={(row) => setSelectedStudentId(+row[0].id.split('_')[0])}// @TODO: way too hacky
            filtersJsx={
              <Button
                theme={styles.colors.accent}
                onPress={() => setIsInviteDialogOpen(true)}
              >
                Invite students
              </Button>
            }
          />

          <Dialog
            visible={_isUnassignedGoalsDialogOpen}
            onDismiss={() => setIsUnassignedGoalsDialogOpen(false)}
          >
            <Dialog.Title>Students with unassigned goals</Dialog.Title>{/* @TODO: i18n */}
            <Dialog.Content>
              {classroomGoals.filter((goal: any) => goal.classroom_goal_metadata.status === 'published').map((goal: any) => { // @TODO: order in reverse-chronological order
                const unassignedStudents = classroomStudents.filter((cs) => {
                  if (cs.status !== 'active') {
                    return false;
                  }

                  return !goal.classroom_goal_metadata.student_ids_assigned?.includes(cs.user_id);
                });

                if (!unassignedStudents.length) {
                  return null;
                }

                const unassignedStudentsTableRows = unassignedStudents.map((cs) => { // @TODO: convert these to accordion? when expanded, show all info for just this student (and class average)
                  return [ // @TODO: update ids
                    {
                      id: `${cs.id}_display_name`,
                      content: (
                        <span>
                          {cs.user ? cs.user.display_name : cs.display_name}{/* @TODO: if user exists, show cs.display_name on hover for convenience? */}
                        </span>
                      ),
                    }, // @TODO: maybe surround with join date (or invitation date if pending)
                    {
                      id: `${cs.id}_assign_button`,
                      content: (// @TODO: too big
                        <Button onPress={() => dispatch(assignGoalToStudent(classroomId, goal.id, cs.user_id))}>
                          Assign goal
                        </Button>
                      ),
                    },
                  ];
                });

                return (
                  <Table// @TODO: rows shouldn't have pointer on hover unless they're selectable
                    // @TODO: this table should be dark background? can barely read title
                    key={goal.id}
                    tableTitle={goal.title}
                    // @TODO: tableSubtitle={goal.date_deadline}
                    open={true}// @TODO: default to false, but have badge for notifications if there are students to approve, for example
                    headerRowData={[
                      {width: 300, id: 'name', content: 'Name'},
                      {width: 100, id: 'assign_button', content: 'Assign goal', rightAlign: true},
                    ]}// @TODO: some of these should be named better and/or have tooltips
                    rowData={unassignedStudentsTableRows}
                    // @TODO: onRowPress={(row) => setSelectedStudentId(+row[0].id.split('_')[0])}// @TODO: way too hacky
                    filtersJsx={
                      <Button
                        theme={styles.colors.accent}
                        onPress={() => {
                          unassignedStudents.forEach((cs) => {
                            dispatch(assignGoalToStudent(classroomId, goal.id, cs.user_id));
                          });
                        }}
                      >
                        Assign all
                      </Button>
                    }
                  />
                );
              })}
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={() => setIsUnassignedGoalsDialogOpen(false)}>
                {i18n('close')}
              </Button>
            </Dialog.Actions>
          </Dialog>

          <InviteDialog
            schoolId={schoolId}
            classroomId={classroomId}
            isOpen={_isInviteDialogOpen}
            onClose={() => setIsInviteDialogOpen(false)}
          />

        </WebsiteSection>
      }

      <WebsiteSection
        isDarkBackground={true}
        title={(
          <span style={styles.titleRow}>
            <span>Goals</span>
            <Button
              theme={styles.colors.accent}
              onPress={() => setIsNewGoalDialogOpen(true)}
              icon='plus'
            >
              New Goal
            </Button>
          </span>
        )}
      >
        {/* @TODO: if no goals, show better empty screen instead of blank section */}
        <View style={styles.goalsWrapper}>
          {classroomGoals.map((goal: any) => <GoalPreview key={goal.id} goal={goal} schoolId={schoolId} classroomId={classroomId} />)}{/* @TODO: on click, open edit dialog or new page? */}
        </View>
      </WebsiteSection>

      {/* @TODO:
      <WebsiteSection
        isDarkBackground={false}
        title={(
          <span style={styles.titleRow}>
            <span>Resources</span>
          </span>
        )}
      >
        <View>
          TODO: links to help docs
          TODO: info about fundraising campaign
        </View>
      </WebsiteSection>
      */}

      {/* @TODO: if no campaigns for this org, display something like "coming soon?" */}

      {/* @TODO: note if this school has private campaigns "log in to view more (if you're a participant)" */}

      {/* @TODO:
      <WebsiteSection
        title='Results'// @TODO: dyanmic ("progress", "results", and something if it hasn't started yet)
      >
        <Text>{"TODO: statistics (nothing too specific though...no money amounts, just maybe wrinkles? or peanuts? or hours and verses reviewed like on the landing page? that's probably the best option...but definitely wrinkles so that sponsors can track it) (if none available yet, hide this section? show something else?)"}</Text>
        <Text>TODO: testimonials</Text>
      </WebsiteSection>
      */}
      <NewGoalDialog
        isOpen={_isNewGoalDialogOpen}
        onClose={() => setIsNewGoalDialogOpen(false)}
        schoolId={schoolId}
        classroomId={classroomId}
      />
    </WebPageStub>
  );
};

export default Classroom;
